import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { X, Trophy, RotateCcw, UserMinus, Share2 } from 'lucide-react';

const ResultModal = ({ result, isOpen, onClose, onSpinAgain, onRemoveWinner, showRemoveOption = true }) => {
  const { t } = useLanguage();

  if (!isOpen || !result) return null;

  const handleShare = async () => {
    const shareData = {
      title: 'wheel.expert Result',
      text: `🎉 The wheel picked: ${result.text}`,
      url: window.location.href
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        // User cancelled or error
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(`${shareData.text}\n${shareData.url}`);
    }
  };

  const handleRemoveAndSpin = () => {
    onRemoveWinner?.(result);
    onSpinAgain?.();
  };

  return (
    <div 
      className="modal-overlay"
      role="dialog"
      aria-labelledby="result-title"
      aria-modal="true"
      onClick={onClose}
      data-testid="result-modal-overlay"
    >
      <div 
        className="modal-content result-modal"
        onClick={(e) => e.stopPropagation()}
        data-testid="result-modal"
      >
        <button
          onClick={onClose}
          className="modal-close"
          aria-label={t('a11y.closeModal')}
          data-testid="result-modal-close"
        >
          <X size={24} />
        </button>

        <div className="result-content">
          <div className="result-icon" aria-hidden="true">
            <Trophy size={48} />
          </div>
          
          <h2 id="result-title" className="result-title">
            {t('wheel.winner')}
          </h2>
          
          <div 
            className="result-value"
            style={{ backgroundColor: result.color }}
            data-testid="result-value"
          >
            {result.text}
          </div>

          <div className="result-actions">
            <button
              onClick={onSpinAgain}
              className="btn-result btn-primary"
              data-testid="spin-again-button"
            >
              <RotateCcw size={20} />
              {t('wheel.spinAgain')}
            </button>
            
            {showRemoveOption && onRemoveWinner && (
              <button
                onClick={handleRemoveAndSpin}
                className="btn-result btn-warning"
                data-testid="remove-and-spin-button"
                title="Remove winner from wheel and spin again"
              >
                <UserMinus size={20} />
                {t('wheel.removeWinner') || 'Remove & Spin'}
              </button>
            )}
            
            <div className="result-secondary-actions">
              <button
                onClick={() => onRemoveWinner?.(result)}
                className="btn-result-small"
                data-testid="remove-winner-button"
              >
                <UserMinus size={16} />
                {t('wheel.removeEntry') || 'Remove'}
              </button>
              
              <button
                onClick={handleShare}
                className="btn-result-small"
                data-testid="share-result-button"
              >
                <Share2 size={16} />
                {t('wheel.share') || 'Share'}
              </button>
              
              <button
                onClick={onClose}
                className="btn-result-small"
                data-testid="close-result-button"
              >
                {t('wheel.close')}
              </button>
            </div>
          </div>
        </div>

        {/* Confetti effect */}
        <div className="confetti" aria-hidden="true">
          {[...Array(30)].map((_, i) => (
            <div 
              key={i} 
              className="confetti-piece"
              style={{
                '--delay': `${Math.random() * 0.5}s`,
                '--x': `${Math.random() * 100}%`,
                '--rotation': `${Math.random() * 720}deg`,
                '--fall-duration': `${1 + Math.random() * 1}s`,
                backgroundColor: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#FFD93D', '#6BCB77'][i % 8]
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ResultModal;
